// Mock data for Customs Complaint System Prototype
// กรมศุลกากร - ระบบลงทะเบียนและบริหารจัดการเรื่องร้องเรียน

export type ComplaintStatus =
  | 'submitted'
  | 'received'
  | 'in_progress'
  | 'pending_info'
  | 'resolved'
  | 'closed';

export type UserRole = 'citizen' | 'staff' | 'admin' | 'executive';

export type Priority = 'low' | 'medium' | 'high' | 'critical';

export interface Category {
  id: string;
  name: string;
  description: string;
  slaHours: number;
}

export interface Department {
  id: string;
  name: string;
  code: string;
}

export interface User {
  id: string;
  name: string;
  email: string;
  phone?: string;
  role: UserRole;
  departmentId?: string;
  isActive: boolean;
  avatar?: string;
}

export interface Complainant {
  name?: string;
  email?: string;
  phone?: string;
  isAnonymous: boolean;
}

export interface Attachment {
  id: string;
  fileName: string;
  fileType: string;
  fileSize: number;
  uploadedAt: string;
}

export interface StatusLog {
  id: string;
  status: ComplaintStatus;
  comment?: string;
  performedBy: string;
  performedAt: string;
  isInternal: boolean;
}

export interface Complaint {
  id: string;
  referenceNumber: string;
  subject: string;
  description: string;
  categoryId: string;
  status: ComplaintStatus;
  priority: Priority;
  complainant: Complainant;
  accusedInfo?: string;
  incidentDate?: string;
  incidentLocation?: string;
  assignedTo?: User;
  attachments: Attachment[];
  statusLogs: StatusLog[];
  createdAt: string;
  updatedAt: string;
  dueDate: string;
}

// ==================== MOCK DATA ====================

export const mockCategories: Category[] = [
  {
    id: '1',
    name: 'ร้องเรียนพฤติกรรมเจ้าหน้าที่',
    description: 'ร้องเรียนเกี่ยวกับพฤติกรรมที่ไม่เหมาะสมของเจ้าหน้าที่',
    slaHours: 48
  },
  {
    id: '2',
    name: 'ร้องเรียนการปฏิบัติหน้าที่',
    description: 'ร้องเรียนเกี่ยวกับการปฏิบัติหน้าที่ที่ไม่ถูกต้อง',
    slaHours: 72
  },
  {
    id: '3',
    name: 'แจ้งเบาะแสการทุจริต',
    description: 'แจ้งเบาะแสเกี่ยวกับการทุจริตประพฤติมิชอบ',
    slaHours: 24
  },
  {
    id: '4',
    name: 'ร้องเรียนขั้นตอนการให้บริการ',
    description: 'ร้องเรียนเกี่ยวกับขั้นตอนหรือกระบวนการให้บริการ',
    slaHours: 48
  },
  {
    id: '5',
    name: 'อื่นๆ',
    description: 'เรื่องร้องเรียนอื่นๆ ที่ไม่อยู่ในประเภทข้างต้น',
    slaHours: 72
  },
];

export const mockDepartments: Department[] = [
  { id: '1', name: 'กองบริหารทั่วไป', code: 'GAD' },
  { id: '2', name: 'กองสืบสวนและปราบปราม', code: 'INV' },
  { id: '3', name: 'กองตรวจสินค้า', code: 'CGI' },
  { id: '4', name: 'กองพิธีการศุลกากร', code: 'CPD' },
  { id: '5', name: 'กองกฎหมาย', code: 'LGL' },
];

export const mockUsers: User[] = [
  {
    id: 'admin1',
    name: 'นายสมศักดิ์ บริหาร',
    email: 'admin@customs.go.th',
    phone: '02-667-6000',
    role: 'admin',
    departmentId: '1',
    isActive: true,
  },
  {
    id: 'exec1',
    name: 'นางสาววิภา ผู้บริหาร',
    email: 'executive@customs.go.th',
    phone: '02-667-6001',
    role: 'executive',
    departmentId: '1',
    isActive: true,
  },
  {
    id: 'staff1',
    name: 'นางสาวสมหญิง รักงาน',
    email: 'staff1@customs.go.th',
    phone: '02-667-7001',
    role: 'staff',
    departmentId: '2',
    isActive: true,
  },
  {
    id: 'staff2',
    name: 'นายสมชาย ดำเนิน',
    email: 'staff2@customs.go.th',
    phone: '02-667-7002',
    role: 'staff',
    departmentId: '3',
    isActive: true,
  },
  {
    id: 'staff3',
    name: 'นางสมใจ ตรวจสอบ',
    email: 'staff3@customs.go.th',
    phone: '02-667-7003',
    role: 'staff',
    departmentId: '2',
    isActive: true,
  },
  {
    id: 'staff4',
    name: 'นายวิชัย พิจารณา',
    email: 'staff4@customs.go.th',
    phone: '02-667-7004',
    role: 'staff',
    departmentId: '4',
    isActive: false,
  },
];

export const mockComplaints: Complaint[] = [
  {
    id: '1',
    referenceNumber: 'CMP-256901-0001',
    subject: 'ร้องเรียนการปฏิบัติหน้าที่ไม่เหมาะสม',
    description: 'เจ้าหน้าที่ไม่ให้ความสะดวกในการดำเนินพิธีการศุลกากร และมีพฤติกรรมไม่สุภาพต่อผู้รับบริการ ต้องรอนานเกินกว่า 3 ชั่วโมง โดยไม่มีคำอธิบายใดๆ',
    categoryId: '1',
    status: 'in_progress',
    priority: 'high',
    complainant: {
      name: 'นายสมชาย ใจดี',
      email: 'somchai@example.com',
      phone: '081-234-5678',
      isAnonymous: false,
    },
    accusedInfo: 'เจ้าหน้าที่ประจำเคาน์เตอร์ที่ 5',
    incidentDate: '2026-01-08',
    incidentLocation: 'ด่านศุลกากรท่าเรือแหลมฉบัง',
    assignedTo: mockUsers.find(u => u.id === 'staff1'),
    attachments: [
      { id: 'a1', fileName: 'หลักฐาน_01.pdf', fileType: 'pdf', fileSize: 2048000, uploadedAt: '2026-01-10T09:30:00Z' },
      { id: 'a2', fileName: 'รูปภาพ_01.jpg', fileType: 'jpg', fileSize: 1024000, uploadedAt: '2026-01-10T09:31:00Z' },
    ],
    statusLogs: [
      { id: 'l1', status: 'submitted', comment: 'ยื่นเรื่องร้องเรียน', performedBy: 'ระบบ', performedAt: '2026-01-10T09:30:00Z', isInternal: false },
      { id: 'l2', status: 'received', comment: 'รับเรื่องและตรวจสอบเอกสาร', performedBy: 'นางสาวสมหญิง รักงาน', performedAt: '2026-01-10T14:00:00Z', isInternal: false },
      { id: 'l3', status: 'in_progress', comment: 'มอบหมายให้ทีมสืบสวนดำเนินการ', performedBy: 'นางสาวสมหญิง รักงาน', performedAt: '2026-01-11T09:00:00Z', isInternal: false },
    ],
    createdAt: '2026-01-10T09:30:00Z',
    updatedAt: '2026-01-11T09:00:00Z',
    dueDate: '2026-01-12T09:30:00Z',
  },
  {
    id: '2',
    referenceNumber: 'CMP-256901-0002',
    subject: 'แจ้งเบาะแสการรับสินบน',
    description: 'พบเห็นเจ้าหน้าที่รับเงินจากผู้นำเข้าเพื่ออำนวยความสะดวกในการตรวจปล่อยสินค้า',
    categoryId: '3',
    status: 'pending_info',
    priority: 'critical',
    complainant: {
      isAnonymous: true,
    },
    accusedInfo: 'ไม่ทราบชื่อ แต่ประจำการที่ด่านศุลกากรสุวรรณภูมิ',
    incidentDate: '2026-01-05',
    incidentLocation: 'ด่านศุลกากรท่าอากาศยานสุวรรณภูมิ',
    assignedTo: mockUsers.find(u => u.id === 'staff3'),
    attachments: [],
    statusLogs: [
      { id: 'l4', status: 'submitted', performedBy: 'ระบบ', performedAt: '2026-01-07T18:45:00Z', isInternal: false },
      { id: 'l5', status: 'received', performedBy: 'นางสมใจ ตรวจสอบ', performedAt: '2026-01-08T09:00:00Z', isInternal: false },
      { id: 'l6', status: 'in_progress', performedBy: 'นางสมใจ ตรวจสอบ', performedAt: '2026-01-08T10:30:00Z', isInternal: false },
      { id: 'l7', status: 'pending_info', comment: 'ขอข้อมูลเพิ่มเติมเกี่ยวกับวันเวลาที่แน่นอน', performedBy: 'นางสมใจ ตรวจสอบ', performedAt: '2026-01-09T14:00:00Z', isInternal: false },
    ],
    createdAt: '2026-01-07T18:45:00Z',
    updatedAt: '2026-01-09T14:00:00Z',
    dueDate: '2026-01-08T18:45:00Z',
  },
  {
    id: '3',
    referenceNumber: 'CMP-256901-0003',
    subject: 'ร้องเรียนความล่าช้าในการตรวจปล่อยสินค้า',
    description: 'สินค้านำเข้าถูกกักไว้นานเกินกว่า 7 วันโดยไม่มีเหตุผลชัดเจน ทำให้เกิดความเสียหายต่อธุรกิจ',
    categoryId: '4',
    status: 'resolved',
    priority: 'medium',
    complainant: {
      name: 'บริษัท สยามอิมปอร์ต จำกัด',
      email: 'contact@siamimport.co.th',
      phone: '02-123-4567',
      isAnonymous: false,
    },
    incidentDate: '2025-12-20',
    incidentLocation: 'ท่าเรือกรุงเทพ',
    assignedTo: mockUsers.find(u => u.id === 'staff2'),
    attachments: [
      { id: 'a3', fileName: 'ใบขนสินค้า.pdf', fileType: 'pdf', fileSize: 512000, uploadedAt: '2026-01-02T10:00:00Z' },
    ],
    statusLogs: [
      { id: 'l8', status: 'submitted', performedBy: 'ระบบ', performedAt: '2026-01-02T10:00:00Z', isInternal: false },
      { id: 'l9', status: 'received', performedBy: 'นายสมชาย ดำเนิน', performedAt: '2026-01-02T11:30:00Z', isInternal: false },
      { id: 'l10', status: 'in_progress', performedBy: 'นายสมชาย ดำเนิน', performedAt: '2026-01-03T09:00:00Z', isInternal: false },
      { id: 'l11', status: 'resolved', comment: 'ประสานงานกับหน่วยงานที่เกี่ยวข้องและดำเนินการปล่อยสินค้าเรียบร้อยแล้ว', performedBy: 'นายสมชาย ดำเนิน', performedAt: '2026-01-05T16:00:00Z', isInternal: false },
    ],
    createdAt: '2026-01-02T10:00:00Z',
    updatedAt: '2026-01-05T16:00:00Z',
    dueDate: '2026-01-04T10:00:00Z',
  },
  {
    id: '4',
    referenceNumber: 'CMP-256901-0004',
    subject: 'ร้องเรียนการเรียกเก็บค่าธรรมเนียมเกินจริง',
    description: 'ถูกเรียกเก็บค่าธรรมเนียมสูงกว่าที่กำหนดไว้ในระเบียบ',
    categoryId: '2',
    status: 'received',
    priority: 'medium',
    complainant: {
      name: 'นางสาวมาลี ประชาชน',
      email: 'malee@gmail.com',
      phone: '089-876-5432',
      isAnonymous: false,
    },
    incidentDate: '2026-01-12',
    incidentLocation: 'ด่านศุลกากรหาดใหญ่',
    attachments: [
      { id: 'a4', fileName: 'ใบเสร็จ.jpg', fileType: 'jpg', fileSize: 768000, uploadedAt: '2026-01-13T08:00:00Z' },
    ],
    statusLogs: [
      { id: 'l12', status: 'submitted', performedBy: 'ระบบ', performedAt: '2026-01-13T08:00:00Z', isInternal: false },
      { id: 'l13', status: 'received', comment: 'รับเรื่องและจะดำเนินการตรวจสอบ', performedBy: 'นายสมชาย ดำเนิน', performedAt: '2026-01-13T10:30:00Z', isInternal: false },
    ],
    createdAt: '2026-01-13T08:00:00Z',
    updatedAt: '2026-01-13T10:30:00Z',
    dueDate: '2026-01-16T08:00:00Z',
  },
  {
    id: '5',
    referenceNumber: 'CMP-256901-0005',
    subject: 'ร้องเรียนระบบออนไลน์ใช้งานยาก',
    description: 'ระบบ e-Customs มีปัญหาในการใช้งาน หน้าจอค้างบ่อย และขั้นตอนซับซ้อนเกินไป',
    categoryId: '5',
    status: 'submitted',
    priority: 'low',
    complainant: {
      name: 'บริษัท ไทยเทรด จำกัด',
      email: 'info@thaitrade.co.th',
      isAnonymous: false,
    },
    incidentDate: '2026-01-14',
    attachments: [],
    statusLogs: [
      { id: 'l14', status: 'submitted', performedBy: 'ระบบ', performedAt: '2026-01-14T11:00:00Z', isInternal: false },
    ],
    createdAt: '2026-01-14T11:00:00Z',
    updatedAt: '2026-01-14T11:00:00Z',
    dueDate: '2026-01-17T11:00:00Z',
  },
  {
    id: '6',
    referenceNumber: 'CMP-256812-0089',
    subject: 'สอบถามเกี่ยวกับการนำเข้าสินค้าควบคุม',
    description: 'ไม่ได้รับคำตอบจากเจ้าหน้าที่เกี่ยวกับขั้นตอนการนำเข้าสินค้าควบคุม',
    categoryId: '4',
    status: 'closed',
    priority: 'low',
    complainant: {
      name: 'นายประสิทธิ์ นำเข้า',
      email: 'prasit@email.com',
      phone: '086-111-2222',
      isAnonymous: false,
    },
    assignedTo: mockUsers.find(u => u.id === 'staff2'),
    attachments: [],
    statusLogs: [
      { id: 'l15', status: 'submitted', performedBy: 'ระบบ', performedAt: '2025-12-15T09:00:00Z', isInternal: false },
      { id: 'l16', status: 'received', performedBy: 'นายสมชาย ดำเนิน', performedAt: '2025-12-15T11:00:00Z', isInternal: false },
      { id: 'l17', status: 'resolved', comment: 'ส่งข้อมูลและขั้นตอนให้ผู้ร้องทางอีเมลแล้ว', performedBy: 'นายสมชาย ดำเนิน', performedAt: '2025-12-16T14:00:00Z', isInternal: false },
      { id: 'l18', status: 'closed', comment: 'ผู้ร้องยืนยันได้รับข้อมูลครบถ้วน', performedBy: 'นายสมชาย ดำเนิน', performedAt: '2025-12-18T10:00:00Z', isInternal: false },
    ],
    createdAt: '2025-12-15T09:00:00Z',
    updatedAt: '2025-12-18T10:00:00Z',
    dueDate: '2025-12-17T09:00:00Z',
  },
];

// ==================== HELPER FUNCTIONS ====================

export const getStatusConfig = (status: ComplaintStatus) => {
  const configs: Record<ComplaintStatus, { label: string; color: string; bgColor: string; icon: string; darkColor: string }> = {
    submitted: { label: 'ยื่นเรื่อง', color: '#64748b', bgColor: '#f1f5f9', icon: 'FileText', darkColor: '#374151' },
    received: { label: 'รับเรื่อง', color: '#0284c7', bgColor: '#e0f2fe', icon: 'CheckCircle', darkColor: '#0c4a6e' },
    in_progress: { label: 'ดำเนินการ', color: '#ca8a04', bgColor: '#fef9c3', icon: 'Clock', darkColor: '#713f12' },
    pending_info: { label: 'รอข้อมูล', color: '#ea580c', bgColor: '#ffedd5', icon: 'AlertCircle', darkColor: '#7c2d12' },
    resolved: { label: 'แล้วเสร็จ', color: '#16a34a', bgColor: '#dcfce7', icon: 'CheckCheck', darkColor: '#14532d' },
    closed: { label: 'ปิดเรื่อง', color: '#475569', bgColor: '#e2e8f0', icon: 'Archive', darkColor: '#1e293b' },
  };
  return configs[status];
};

export const getPriorityConfig = (priority: Priority) => {
  const configs: Record<Priority, { label: string; color: string; bgColor: string }> = {
    low: { label: 'ต่ำ', color: '#64748b', bgColor: '#f1f5f9' },
    medium: { label: 'ปานกลาง', color: '#ca8a04', bgColor: '#fef9c3' },
    high: { label: 'สูง', color: '#ea580c', bgColor: '#ffedd5' },
    critical: { label: 'เร่งด่วน', color: '#dc2626', bgColor: '#fee2e2' },
  };
  return configs[priority];
};

export const getCategoryById = (id: string) => mockCategories.find(c => c.id === id);
export const getUserById = (id: string) => mockUsers.find(u => u.id === id);
export const getDepartmentById = (id: string) => mockDepartments.find(d => d.id === id);
export const getComplaintByRef = (ref: string) => mockComplaints.find(c => c.referenceNumber === ref);

// Dashboard stats
export const getDashboardStats = () => {
  const now = new Date();
  return {
    submitted: mockComplaints.filter(c => c.status === 'submitted').length,
    inProgress: mockComplaints.filter(c => c.status === 'in_progress').length,
    pendingInfo: mockComplaints.filter(c => c.status === 'pending_info').length,
    nearSla: mockComplaints.filter(c => {
      const due = new Date(c.dueDate);
      const diff = due.getTime() - now.getTime();
      return diff > 0 && diff < 24 * 60 * 60 * 1000 && !['resolved', 'closed'].includes(c.status);
    }).length,
    overdue: mockComplaints.filter(c => {
      const due = new Date(c.dueDate);
      return due < now && !['resolved', 'closed'].includes(c.status);
    }).length,
    resolvedThisMonth: mockComplaints.filter(c => {
      const updated = new Date(c.updatedAt);
      return c.status === 'resolved' &&
        updated.getMonth() === now.getMonth() &&
        updated.getFullYear() === now.getFullYear();
    }).length,
    closedThisMonth: mockComplaints.filter(c => {
      const updated = new Date(c.updatedAt);
      return c.status === 'closed' &&
        updated.getMonth() === now.getMonth() &&
        updated.getFullYear() === now.getFullYear();
    }).length,
    total: mockComplaints.length,
  };
};

// Chart data (hex colors for Recharts)
export const getCategoryChartData = () => {
  return mockCategories.map((cat, index) => ({
    name: cat.name.length > 20 ? cat.name.substring(0, 20) + '...' : cat.name,
    count: mockComplaints.filter(c => c.categoryId === cat.id).length,
    fill: ['#1e3a5f', '#c9a227', '#16a34a', '#0284c7', '#ea580c'][index],
  }));
};

export const getStatusChartData = () => {
  const statuses: ComplaintStatus[] = ['submitted', 'received', 'in_progress', 'pending_info', 'resolved', 'closed'];
  return statuses.map(status => ({
    name: getStatusConfig(status).label,
    value: mockComplaints.filter(c => c.status === status).length,
    fill: getStatusConfig(status).color,
  }));
};

export const getMonthlyTrendData = () => {
  return [
    { month: 'ก.ย.', received: 45, closed: 38 },
    { month: 'ต.ค.', received: 52, closed: 48 },
    { month: 'พ.ย.', received: 48, closed: 45 },
    { month: 'ธ.ค.', received: 61, closed: 55 },
    { month: 'ม.ค.', received: 38, closed: 22 },
  ];
};
